import { isEmpty } from "lodash";
import FiltersBox from "PFApp/components/filters/filters_box";
import FiltersButton from "PFApp/components/filters/filters_button";
import FiltersPreview from "PFApp/components/filters/filters_preview";
import { checkFiltersAreClear } from "PFApp/use_filtered_collection/format_filters";
import { Button } from "PFComponents/button";
import ButtonLink from "PFComponents/button/button_link";
import { Card } from "PFComponents/card";
import DataTable from "PFComponents/data_table/data_table";
import { LoadingDots } from "PFComponents/loading_dots";
import NotFoundComponent from "PFComponents/not_found/not_found";
import SimplePagination from "PFComponents/pagination/simple_pagination";
import sortableHeader from "PFComponents/sortable_header/sortable_header";
import { isSaasAdmin } from "PFCore/helpers/profile";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { ApiRoute } from "PFCore/utilities/routes";
import PropTypes from "prop-types";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { truncate } from "underscore.string";

import { VALUE_TYPES_CONTENT_DISABLED, VALUE_TYPES_IMPORT_DISABLED } from "../custom_values.consts";
import css from "./custom_fields.less";
import { StoredAsCell } from "./stored_as_cell";

const CustomFieldsListPage = ({
  pending,
  errors,
  total,
  totalPages,
  currentPage,
  filters,
  handlePageChange,
  handleFilterChange,
  customFields,
  applySort,
  order,
  clearFilters
}) => {
  const { t } = useTranslation("admin", { keyPrefix: "customFields" });
  const history = useHistory();
  const [filtersShown, setFiltersShown] = useState(false);
  const { data: currentProfile } = useCurrentProfile();

  const isUserSaasAdmin = isSaasAdmin(currentProfile);

  const makeHeader = useCallback(
    (title, name) => () => sortableHeader(title, name, applySort, order),
    [applySort, order]
  );

  const kindLabels = useMemo(
    () => ({
      single: t("kindOptions.single"),
      multiple: t("kindOptions.multiple")
    }),
    [t]
  );

  const newValuesModeLabels = useMemo(
    () => ({
      allowed: t("newValuesAllowed"),
      not_allowed: t("newValuesNotAllowed"),
      require_approval: t("newValuesRequireApproval")
    }),
    [t]
  );

  const cellRenderer = (attribute) => <span>{attribute}</span>;

  const renderActions = (item) => (
    <div className={css.actions}>
      {!VALUE_TYPES_CONTENT_DISABLED.includes(item.value_type) && (
        <ButtonLink
          kind="secondary"
          small
          style={{ margin: "8px" }}
          href={`/admin/custom_fields/${item.id}/content`}
        >
          {t("listButtons.dictionaries")}
        </ButtonLink>
      )}
      {isUserSaasAdmin && (
        <>
          {!VALUE_TYPES_IMPORT_DISABLED.includes(item.value_type) && (
            <ButtonLink
              kind="secondary"
              small
              style={{ margin: "8px" }}
              href={`/admin/custom_fields/${item.id}/content/import`}
            >
              {t("listButtons.import")}
            </ButtonLink>
          )}
          <ButtonLink
            kind="secondary"
            small
            style={{ margin: "8px" }}
            href={`/admin/custom_fields/${item.id}/edit`}
          >
            {t("listButtons.edit")}
          </ButtonLink>
          <Button
            kind="danger"
            small
            style={{ margin: "8px" }}
            onClick={() => {
              if (confirm(t("types.removeConfirm"))) {
                $.ajax({
                  api_version: 2,
                  url: ApiRoute(`/api/custom_types/${item.id}`),
                  method: "DELETE"
                })
                  .then(() => history.go(0))
                  .catch(() => alert(t("types.inUseError")));
              }
            }}
          >
            {t("listButtons.remove")}
          </Button>
        </>
      )}
    </div>
  );

  const columns = [
    isUserSaasAdmin
      ? {
          renderHeader: makeHeader(t("table.headers.storedAs"), "name"),
          renderCell: (data) => <StoredAsCell name={data.name} isOrderable={data.orderable} />
        }
      : {},
    {
      renderHeader: makeHeader(t("table.headers.displayAs"), "display_as"),
      renderCell: (data) => cellRenderer(data?.display_as)
    },
    isUserSaasAdmin
      ? {
          renderHeader: makeHeader(t("table.headers.description"), "description"),
          renderCell: (data) => cellRenderer(data?.description ? truncate(data?.description, 32) : "")
        }
      : {},
    {
      renderHeader: makeHeader(t("table.headers.kind"), "kind"),
      renderCell: (data) => cellRenderer(kindLabels[data?.kind] ?? data?.kind)
    },
    {
      renderHeader: makeHeader(t("table.headers.position"), "position"),
      renderCell: (data) => cellRenderer(data?.position)
    },
    {
      renderHeader: makeHeader(t("table.headers.unapproved"), "not_approved_nor_curated"),
      renderCell: (data) =>
        cellRenderer(
          data.new_values === "require_approval"
            ? data?.not_approved_nor_curated
              ? data?.not_approved_nor_curated
              : "N/A"
            : "N/A"
        )
    },
    {
      renderHeader: makeHeader(t("table.headers.uncurated"), "not_curated"),
      renderCell: (data) =>
        cellRenderer(
          data.new_values === "require_approval" ? (data?.not_curated ? data?.not_curated : "N/A") : "N/A"
        )
    },
    {
      renderHeader: makeHeader(t("table.headers.total"), "custom_values_count"),
      renderCell: (data) => cellRenderer(data?.custom_values_count)
    },
    {
      renderHeader: makeHeader(t("table.headers.newValues"), "new_values"),
      renderCell: (data) => cellRenderer(newValuesModeLabels[data?.new_values] ?? data?.new_values)
    },
    {
      headerName: t("table.headers.actions"),
      renderCell: (data) => renderActions(data),
      narrow: true
    }
  ];

  const filtersAreClear = checkFiltersAreClear(filters);

  return (
    <div className={css.root} style={{ opacity: pending ? 0.7 : 1 }}>
      {!pending && !isEmpty(errors) ? (
        <NotFoundComponent
          message={t("pageNotFound.message")}
          buttonMessage={t("pageNotFound.buttonMessage")}
          buttonUrl="/admin/custom_fields"
        />
      ) : (
        <>
          <div className={css.header}>
            <h1>{t("title", { count: total })}</h1>
          </div>
          <div className={css.controls}>
            <div className={css.filtersPreviewWrap}>
              <FiltersPreview
                meta={{ order: {}, filters }}
                filtersAreClear={filtersAreClear}
                clearFilters={clearFilters}
                onFilterChange={handleFilterChange}
                onMoreClicked={() => setFiltersShown(true)}
              />
            </div>
            <div className={css.buttonsContainer}>
              {isUserSaasAdmin && (
                <ButtonLink href="/admin/custom_fields/new">{t("newCustomField")}</ButtonLink>
              )}
              <FiltersButton
                style={{ paddingLeft: "5px" }}
                filtersShown={filtersShown}
                filtersAreClear={filtersAreClear}
                onClick={() => setFiltersShown(!filtersShown)}
                withLabel
              />
            </div>
          </div>
          {filtersShown && (
            <div className={css.filtersContainer}>
              <FiltersBox
                isLoading={pending}
                closeFilters={() => setFiltersShown(false)}
                meta={{ search_id: -1, filters }}
                filtersAreClear={filtersAreClear}
                clearFilters={clearFilters}
                handleFilterChange={handleFilterChange}
              />
            </div>
          )}
          {pending && <LoadingDots className={css.loadingDots} />}
          {!pending && customFields.length === 0 && (
            <div className={css.noCustomFields}>{t("noCustomFieldsFound")}</div>
          )}
          {!pending && customFields.length > 0 && (
            <>
              <Card paddingVariant="none">
                <DataTable style={{ whiteSpace: "nowrap" }} columns={columns} data={customFields} />
              </Card>
              <SimplePagination
                currentPage={currentPage}
                totalPages={totalPages}
                handleClick={handlePageChange}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

CustomFieldsListPage.propTypes = {
  pending: PropTypes.bool,
  errors: PropTypes.array,
  customFields: PropTypes.array,
  total: PropTypes.number,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  filters: PropTypes.array.isRequired,
  requestFilters: PropTypes.object,
  handlePageChange: PropTypes.func.isRequired,
  handleFilterChange: PropTypes.func.isRequired,
  applySort: PropTypes.func.isRequired,
  order: PropTypes.string,
  clearFilters: PropTypes.func
};

export default CustomFieldsListPage;
